import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const FORM_SELECTOR =
    '[data-map-suggestion-form]';

function parseCoordinate(value) {
    const parsed = Number.parseFloat(value);

    return Number.isFinite(parsed)
        ? parsed
        : null;
}

function parseJsonArray(value) {
    try {
        const parsed = JSON.parse(
            value || '[]'
        );

        return Array.isArray(parsed)
            ? parsed
            : [];
    } catch {
        return [];
    }
}

function parsePath(value) {
    try {
        const parsed = JSON.parse(
            value || '[]'
        );

        if (!Array.isArray(parsed)) {
            return [];
        }

        return parsed
            .map((point) => {
                const latitude =
                    parseCoordinate(
                        point?.latitude
                    );

                const longitude =
                    parseCoordinate(
                        point?.longitude
                    );

                if (
                    latitude === null
                    || longitude === null
                ) {
                    return null;
                }

                return {
                    latitude,
                    longitude,
                };
            })
            .filter(Boolean);
    } catch {
        return [];
    }
}

function initializeForm(form) {
    if (
        form.dataset
            .mapSuggestionPathReady
        === 'true'
    ) {
        return;
    }

    const typeSelect = form.querySelector(
        '[data-map-suggestion-type]'
    );

    const section = form.querySelector(
        '[data-map-suggestion-path-section]'
    );

    const picker = form.querySelector(
        '[data-map-suggestion-path-picker]'
    );

    const mapElement = form.querySelector(
        '[data-map-suggestion-path-map]'
    );

    const pathInput = form.querySelector(
        '[data-map-suggestion-path-points]'
    );

    const pointCount = form.querySelector(
        '[data-map-suggestion-path-point-count]'
    );

    const status = form.querySelector(
        '[data-map-suggestion-path-status]'
    );

    const undoButton = form.querySelector(
        '[data-map-suggestion-path-undo]'
    );

    const clearButton = form.querySelector(
        '[data-map-suggestion-path-clear]'
    );

    const startButton = form.querySelector(
        '[data-map-suggestion-path-start]'
    );

    const finishButton = form.querySelector(
        '[data-map-suggestion-path-finish]'
    );

    const currentLocationButton = form.querySelector(
        '[data-map-suggestion-path-current-location]'
    );

    const distanceLabel = form.querySelector(
        '[data-map-suggestion-path-distance]'
    );

    if (
        !typeSelect
        || !section
        || !picker
        || !mapElement
        || !pathInput
    ) {
        return;
    }

    form.dataset
        .mapSuggestionPathReady =
            'true';

    const existingPaths =
        parseJsonArray(
            picker.dataset.existingPaths
        );

    const existingJunctions =
        parseJsonArray(
            picker.dataset.existingJunctions
        );

    const SNAP_DISTANCE_PX = 18;

    const minimumPoints =
        Number.parseInt(
            picker.dataset.pathSketchMinPoints
            || '2',
            10
        );

    const maximumPoints =
        Number.parseInt(
            picker.dataset.pathSketchMaxPoints
            || '250',
            10
        );

    let map = null;
    let polyline = null;
    let pointMarkers = [];
    let points = parsePath(
        pathInput.value
    );

    let drawingActive =
        points.length > 0
        || picker.dataset
            .pathSketchAutostart
            === 'true';

    const syncInput = () => {
        pathInput.value =
            JSON.stringify(
                points.map((point) => ({
                    latitude: Number(
                        point.latitude.toFixed(7)
                    ),
                    longitude: Number(
                        point.longitude.toFixed(7)
                    ),
                }))
            );
    };

    const segmentDistanceMeters = (
        from,
        to
    ) => {
        const earthRadius = 6371000;
        const toRadians = (degrees) =>
            degrees * Math.PI / 180;

        const latitude1 =
            toRadians(from.latitude);
        const latitude2 =
            toRadians(to.latitude);

        const deltaLatitude =
            toRadians(
                to.latitude
                - from.latitude
            );

        const deltaLongitude =
            toRadians(
                to.longitude
                - from.longitude
            );

        const a =
            Math.sin(deltaLatitude / 2) ** 2
            + Math.cos(latitude1)
                * Math.cos(latitude2)
                * Math.sin(deltaLongitude / 2) ** 2;

        return earthRadius
            * 2
            * Math.atan2(
                Math.sqrt(a),
                Math.sqrt(1 - a)
            );
    };

    const approximateDistanceMeters = () => {
        let distance = 0;

        for (
            let index = 1;
            index < points.length;
            index += 1
        ) {
            distance += segmentDistanceMeters(
                points[index - 1],
                points[index]
            );
        }

        return distance;
    };

    const formatDistance = (meters) => {
        const feet = meters * 3.28084;

        if (feet < 1000) {
            return `${Math.round(feet)} ft`;
        }

        return `${(feet / 5280).toFixed(2)} mi`;
    };

    const updateStatus = () => {
        const count = points.length;

        if (pointCount) {
            pointCount.textContent =
                `${count} ${count === 1 ? 'point' : 'points'}`;
        }

        if (distanceLabel) {
            distanceLabel.textContent =
                formatDistance(
                    approximateDistanceMeters()
                );
        }

        startButton?.toggleAttribute(
            'disabled',
            drawingActive
        );

        finishButton?.toggleAttribute(
            'disabled',
            !drawingActive
            || count < minimumPoints
        );

        undoButton?.toggleAttribute(
            'disabled',
            count === 0
        );

        clearButton?.toggleAttribute(
            'disabled',
            count === 0
        );

        if (!status) {
            return;
        }

        if (!drawingActive && count === 0) {
            status.textContent =
                'Choose Start Route, then tap the map to place path points.';
        } else if (!drawingActive && count >= minimumPoints) {
            status.textContent =
                'Route sketch finished. Start Route to add more points, or submit it for organizer review.';
        } else if (count === 0) {
            status.textContent =
                'Tap the map to place the first route point.';
        } else if (count < minimumPoints) {
            status.textContent =
                'Add at least one more point to define the route.';
        } else if (count >= maximumPoints) {
            status.textContent =
                'Maximum route detail reached. Finish the route or submit it.';
        } else {
            status.textContent =
                'Keep tapping to follow the observed walkway, or choose Finish Route when the sketch is complete.';
        }
    };

    const clearMarkers = () => {
        if (!map) {
            return;
        }

        pointMarkers.forEach((marker) => {
            map.removeLayer(marker);
        });

        pointMarkers = [];
    };

    const render = (
        fit = false
    ) => {
        if (!map) {
            return;
        }

        if (polyline) {
            map.removeLayer(polyline);
            polyline = null;
        }

        clearMarkers();

        points.forEach((point, index) => {
            const marker =
                L.circleMarker(
                    [
                        point.latitude,
                        point.longitude,
                    ],
                    {
                        radius: 6,
                        color: '#ffffff',
                        weight: 2,
                        fillColor: '#2563eb',
                        fillOpacity: 1,
                    }
                )
                    .addTo(map)
                    .bindTooltip(
                        `Point ${index + 1}`,
                        {
                            direction: 'top',
                        }
                    );

            pointMarkers.push(marker);
        });

        if (points.length >= 2) {
            polyline =
                L.polyline(
                    points.map((point) => [
                        point.latitude,
                        point.longitude,
                    ]),
                    {
                        color: '#2563eb',
                        weight: 5,
                        opacity: 0.9,
                    }
                ).addTo(map);
        }

        syncInput();
        updateStatus();

        if (
            fit
            && points.length > 0
        ) {
            const bounds =
                L.latLngBounds(
                    points.map((point) => [
                        point.latitude,
                        point.longitude,
                    ])
                );

            if (bounds.isValid()) {
                map.fitBounds(
                    bounds,
                    {
                        padding: [40, 40],
                        maxZoom: 18,
                        animate: false,
                    }
                );
            }
        }
    };

    const ensureMap = () => {
        if (map) {
            window.setTimeout(
                () => map.invalidateSize(),
                50
            );

            return;
        }

        const centerLatitude =
            parseCoordinate(
                picker.dataset.centerLatitude
            );

        const centerLongitude =
            parseCoordinate(
                picker.dataset.centerLongitude
            );

        map = L.map(
            mapElement,
            {
                zoomControl: true,
                scrollWheelZoom: false,
            }
        ).setView(
            [
                centerLatitude ?? 0,
                centerLongitude ?? 0,
            ],
            centerLatitude !== null
                && centerLongitude !== null
                ? 17
                : 2
        );

        L.tileLayer(
            'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
            {
                maxZoom: 20,
                attribution:
                    '&copy; OpenStreetMap contributors',
            }
        ).addTo(map);

        const contextLayer =
            L.layerGroup().addTo(map);

        existingPaths.forEach(
            (existingPath) => {
                const pathPoints =
                    Array.isArray(
                        existingPath.path_points
                    )
                        ? existingPath.path_points
                            .map((point) => {
                                const latitude =
                                    parseCoordinate(
                                        point?.latitude
                                    );
                                const longitude =
                                    parseCoordinate(
                                        point?.longitude
                                    );

                                return (
                                    latitude !== null
                                    && longitude !== null
                                )
                                    ? [
                                        latitude,
                                        longitude,
                                    ]
                                    : null;
                            })
                            .filter(Boolean)
                        : [];

                if (pathPoints.length < 2) {
                    return;
                }

                L.polyline(
                    pathPoints,
                    {
                        color: '#64748b',
                        weight: 4,
                        opacity: 0.65,
                    }
                )
                    .addTo(contextLayer)
                    .bindTooltip(
                        `${existingPath.name || 'Official path'}${
                            existingPath.type
                                ? ` · ${existingPath.type}`
                                : ''
                        }`,
                        {
                            sticky: true,
                        }
                    );
            }
        );

        const junctionMarkers =
            new Map();

        existingJunctions.forEach(
            (junction) => {
                const latitude =
                    parseCoordinate(
                        junction.latitude
                    );
                const longitude =
                    parseCoordinate(
                        junction.longitude
                    );

                if (
                    latitude === null
                    || longitude === null
                ) {
                    return;
                }

                const marker =
                    L.circleMarker(
                        [
                            latitude,
                            longitude,
                        ],
                        {
                            radius: 7,
                            color: '#ffffff',
                            weight: 2,
                            fillColor: '#4f46e5',
                            fillOpacity: 0.95,
                        }
                    )
                        .addTo(contextLayer)
                        .bindTooltip(
                            `${junction.name || 'Junction'}${
                                junction.type
                                    ? ` · ${junction.type}`
                                    : ''
                            }`,
                            {
                                direction: 'top',
                            }
                        );

                junctionMarkers.set(
                    Number(junction.id),
                    marker
                );
            }
        );

        const nearestJunctionToClick =
            (latlng) => {
                const clickPoint =
                    map.latLngToContainerPoint(
                        latlng
                    );

                let nearest = null;
                let nearestDistance =
                    Number.POSITIVE_INFINITY;

                existingJunctions.forEach(
                    (junction) => {
                        const latitude =
                            parseCoordinate(
                                junction.latitude
                            );
                        const longitude =
                            parseCoordinate(
                                junction.longitude
                            );

                        if (
                            latitude === null
                            || longitude === null
                        ) {
                            return;
                        }

                        const junctionPoint =
                            map.latLngToContainerPoint(
                                [
                                    latitude,
                                    longitude,
                                ]
                            );

                        const distance =
                            clickPoint.distanceTo(
                                junctionPoint
                            );

                        if (
                            distance
                            < nearestDistance
                        ) {
                            nearestDistance =
                                distance;
                            nearest = {
                                ...junction,
                                latitude,
                                longitude,
                            };
                        }
                    }
                );

                return (
                    nearest
                    && nearestDistance
                        <= SNAP_DISTANCE_PX
                )
                    ? nearest
                    : null;
            };

        let highlightedJunctionId = null;

        const clearSnapHighlight = () => {
            if (
                highlightedJunctionId
                === null
            ) {
                return;
            }

            const marker =
                junctionMarkers.get(
                    highlightedJunctionId
                );

            marker?.setStyle({
                radius: 7,
                color: '#ffffff',
                weight: 2,
                fillColor: '#4f46e5',
                fillOpacity: 0.95,
            });

            highlightedJunctionId = null;
        };

        const highlightSnapJunction =
            (junction) => {
                const junctionId =
                    Number(junction?.id);

                if (
                    ! Number.isFinite(
                        junctionId
                    )
                ) {
                    clearSnapHighlight();
                    return;
                }

                if (
                    highlightedJunctionId
                    === junctionId
                ) {
                    return;
                }

                clearSnapHighlight();

                const marker =
                    junctionMarkers.get(
                        junctionId
                    );

                marker?.setStyle({
                    radius: 10,
                    color: '#f59e0b',
                    weight: 3,
                    fillColor: '#f59e0b',
                    fillOpacity: 1,
                });

                marker?.bringToFront();

                highlightedJunctionId =
                    junctionId;
            };

        map.on(
            'mousemove',
            (event) => {
                const junction =
                    nearestJunctionToClick(
                        event.latlng
                    );

                if (junction) {
                    highlightSnapJunction(
                        junction
                    );
                } else {
                    clearSnapHighlight();
                }
            }
        );

        map.on(
            'mouseout',
            clearSnapHighlight
        );

        map.on(
            'click',
            (event) => {
                if (!drawingActive) {
                    if (status) {
                        status.textContent =
                            'Choose Start Route before adding another waypoint.';
                    }

                    return;
                }

                if (
                    points.length
                    >= maximumPoints
                ) {
                    if (status) {
                        status.textContent =
                            `A route sketch can contain up to ${maximumPoints} points.`;
                    }

                    return;
                }

                const snappedJunction =
                    nearestJunctionToClick(
                        event.latlng
                    );

                points.push({
                    latitude:
                        snappedJunction
                            ? snappedJunction.latitude
                            : event.latlng.lat,
                    longitude:
                        snappedJunction
                            ? snappedJunction.longitude
                            : event.latlng.lng,
                });

                render();

                if (
                    snappedJunction
                    && status
                ) {
                    status.textContent =
                        `Snapped to official junction: ${
                            snappedJunction.name
                            || 'Junction'
                        }.`;
                }
            }
        );

        render(
            points.length > 0
        );

        window.setTimeout(
            () => map.invalidateSize(),
            100
        );
    };

    const updateVisibility = () => {
        const visible =
            typeSelect.value
            === 'new_path'
            || typeSelect.value
                === 'path_correction';

        section.classList.toggle(
            'hidden',
            !visible
        );

        if (visible) {
            ensureMap();
        }
    };

    startButton?.addEventListener(
        'click',
        () => {
            drawingActive = true;
            updateStatus();
            ensureMap();

            if (status) {
                status.textContent =
                    points.length === 0
                        ? 'Route drawing started. Tap the map to add the first waypoint.'
                        : 'Route drawing resumed. Tap the map to add more waypoints.';
            }
        }
    );

    finishButton?.addEventListener(
        'click',
        () => {
            if (
                points.length
                < minimumPoints
            ) {
                return;
            }

            drawingActive = false;
            updateStatus();
        }
    );

    currentLocationButton?.addEventListener(
        'click',
        () => {
            if (
                !navigator.geolocation
            ) {
                if (status) {
                    status.textContent =
                        'Current location is not available in this browser. You can still tap the map manually.';
                }

                return;
            }

            if (
                points.length
                >= maximumPoints
            ) {
                if (status) {
                    status.textContent =
                        `A route sketch can contain up to ${maximumPoints} points.`;
                }

                return;
            }

            drawingActive = true;
            currentLocationButton.disabled = true;

            if (status) {
                status.textContent =
                    'Requesting one current-location waypoint…';
            }

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    points.push({
                        latitude:
                            position.coords.latitude,
                        longitude:
                            position.coords.longitude,
                    });

                    render();
                    ensureMap();

                    const latest =
                        points[
                            points.length - 1
                        ];

                    map?.setView(
                        [
                            latest.latitude,
                            latest.longitude,
                        ],
                        Math.max(
                            map.getZoom(),
                            17
                        ),
                        {
                            animate: false,
                        }
                    );

                    currentLocationButton.disabled = false;

                    if (status) {
                        status.textContent =
                            'Added one current-location waypoint. No continuous tracking was started.';
                    }
                },
                () => {
                    currentLocationButton.disabled = false;

                    if (status) {
                        status.textContent =
                            'FestFriends could not read your current location. Tap the map to place the waypoint instead.';
                    }
                },
                {
                    enableHighAccuracy: true,
                    maximumAge: 15000,
                    timeout: 10000,
                }
            );
        }
    );

    undoButton?.addEventListener(
        'click',
        () => {
            if (points.length === 0) {
                return;
            }

            points.pop();
            render();
        }
    );

    clearButton?.addEventListener(
        'click',
        () => {
            points = [];
            drawingActive = false;
            render();
        }
    );

    typeSelect.addEventListener(
        'change',
        updateVisibility
    );

    form.addEventListener(
        'submit',
        (event) => {
            const isNewPath =
                typeSelect.value
                === 'new_path';

            const isPathCorrection =
                typeSelect.value
                === 'path_correction';

            if (
                !isNewPath
                && !isPathCorrection
            ) {
                pathInput.value = '';
                return;
            }

            if (
                isNewPath
                && points.length
                    < minimumPoints
            ) {
                event.preventDefault();

                if (status) {
                    status.textContent =
                        `Draw at least ${minimumPoints} points before submitting the new path.`;
                }

                section.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });

                return;
            }

            if (
                isPathCorrection
                && points.length === 1
            ) {
                event.preventDefault();

                if (status) {
                    status.textContent =
                        'Either clear the replacement route or draw at least two points.';
                }

                section.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });

                return;
            }

            syncInput();
        }
    );

    updateStatus();
    updateVisibility();
}

function initializeForms() {
    document
        .querySelectorAll(FORM_SELECTOR)
        .forEach(initializeForm);
}

if (
    document.readyState === 'loading'
) {
    document.addEventListener(
        'DOMContentLoaded',
        initializeForms,
        {
            once: true,
        }
    );
} else {
    initializeForms();
}

document.addEventListener(
    'livewire:navigated',
    initializeForms
);
